import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { Box, Typography } from "@mui/material";
import { Theme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useColorMode } from "src/design/ColorModeProvider";
import { radius } from "src/design/tokens";
import { fontStretch } from "src/design/typography";

export type TallyKey =
  | "presidential"
  | "national"
  | "participation"
  | "incomplete"
  | "pending";

export interface TallyRowProps {
  counts: Partial<Record<TallyKey, number>>;
  /** Awards are not final yet: shows the "Awards lock when you finalize" note. */
  locked?: boolean;
  /**
   * `pills` (default): the review header's white pills with a colored dot (design `tallies`).
   * `bars`: the session page's dark "Award outlook" card with a bar per tier.
   */
  variant?: "pills" | "bars";
  /** Denominator for the bars variant (students in the session). */
  total?: number;
}

const ORDER: TallyKey[] = [
  "presidential",
  "national",
  "participation",
  "incomplete",
  "pending",
];

const colorFor = (theme: Theme, key: TallyKey): string => {
  const p = theme.palette;
  switch (key) {
    case "presidential":
      return p.tierPresidential;
    case "national":
      return p.tierNational;
    case "participation":
      return p.gold;
    case "incomplete":
      return p.tokens.warningFg;
    default:
      return p.tokens.textFaint;
  }
};

/** Award tallies for a session: label and count per tier. */
export const TallyRow = ({
  counts,
  locked = false,
  variant = "pills",
  total,
}: TallyRowProps) => {
  const { t } = useTranslation();
  const { mode } = useColorMode();
  const labels: Record<TallyKey, string> = {
    presidential: t("awards.presidential", { defaultValue: "Presidential" }),
    national: t("awards.national", { defaultValue: "National" }),
    participation: t("awards.participation", { defaultValue: "Participation" }),
    incomplete: t("awards.incomplete", { defaultValue: "Incomplete" }),
    pending: t("awards.pending", { defaultValue: "In progress" }),
  };
  const lockNote = locked ? (
    <Typography
      component="p"
      sx={{
        m: 0,
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        fontSize: 11.5,
        fontWeight: 500,
        color: variant === "bars" ? "heroMuted" : "text.secondary",
      }}
    >
      <LockOutlinedIcon aria-hidden="true" sx={{ fontSize: 14 }} />
      {t("review.awardsLocked", {
        defaultValue: "Awards lock when you finalize.",
      })}
    </Typography>
  ) : null;

  if (variant === "bars") {
    const denominator =
      total && total > 0
        ? total
        : Math.max(
            1,
            ORDER.reduce((sum, key) => sum + (counts[key] ?? 0), 0),
          );
    return (
      <Box
        data-testid="tally-row"
        sx={(theme) => ({
          bgcolor:
            mode === "dark"
              ? theme.palette.tokens.surfaceAlt
              : theme.palette.tokens.strong,
          color:
            mode === "dark"
              ? theme.palette.tokens.text
              : theme.palette.tokens.onStrong,
          borderRadius: `${radius.card}px`,
          p: "18px 20px",
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          boxShadow: theme.shadows[1],
        })}
      >
        <Box
          role="list"
          sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}
        >
          {ORDER.map((key) => {
            const n = counts[key] ?? 0;
            const pct = Math.min(100, Math.round((n / denominator) * 100));
            return (
              <Box
                key={key}
                role="listitem"
                data-tally={key}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "96px minmax(0, 1fr) 30px",
                  gap: 1.25,
                  alignItems: "center",
                  fontSize: 12.5,
                  fontWeight: 600,
                }}
              >
                <span>{labels[key]}</span>
                <Box
                  aria-hidden
                  sx={(theme) => ({
                    height: 10,
                    borderRadius: `${radius.pill}px`,
                    bgcolor:
                      mode === "dark"
                        ? theme.palette.tokens.surfaceSunken
                        : theme.palette.tokens.textMuted,
                    overflow: "hidden",
                  })}
                >
                  <Box
                    sx={(theme) => ({
                      height: "100%",
                      width: `${pct}%`,
                      borderRadius: `${radius.pill}px`,
                      bgcolor: colorFor(theme, key),
                      transition: "width .6s",
                    })}
                  />
                </Box>
                <Box
                  component="b"
                  sx={{
                    fontSize: 18,
                    fontWeight: 800,
                    fontStretch: fontStretch.condensed,
                    textAlign: "right",
                  }}
                >
                  {n}
                </Box>
              </Box>
            );
          })}
        </Box>
        {lockNote}
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Box
        role="list"
        data-testid="tally-row"
        sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}
      >
        {ORDER.map((key) => (
          <Box
            key={key}
            role="listitem"
            data-tally={key}
            sx={(theme) => ({
              display: "flex",
              gap: 1,
              alignItems: "center",
              px: 1.5,
              py: 1,
              borderRadius: `${radius.pill}px`,
              bgcolor: theme.palette.tokens.surface,
              boxShadow: theme.shadows[1],
              fontSize: 13,
              fontWeight: 700,
            })}
          >
            <Box
              aria-hidden
              sx={(theme) => ({
                width: 10,
                height: 10,
                borderRadius: "50%",
                bgcolor: colorFor(theme, key),
              })}
            />
            {labels[key]}
            <Box
              component="b"
              sx={{
                fontSize: 15,
                fontWeight: 800,
                fontStretch: fontStretch.condensed,
              }}
            >
              {counts[key] ?? 0}
            </Box>
          </Box>
        ))}
      </Box>
      {lockNote}
    </Box>
  );
};

export default TallyRow;
